import {
  PropsWithChildren,
  useContext,
  Dispatch,
  createContext,
  useReducer,
  useEffect,
  useRef,
  useCallback
} from 'react';
import {
  carManagmentReducer,
  CarManagment_ACTION_TYPE,
  SET_CAR_EVENTS
} from 'reducers/CarManagmentReducer';
import axios from 'axios';
import { PlannerDateRange } from 'helpers/utils';

export interface Trip {
  outsideUsers: number;
  id: number;
  grupo: number;
  title: string;
  companiesList: string; //companies select for alteration
  usersList: string;
  start: Date;
  end: Date;
  end_registered: Date | null;
  start_registered: Date | null;
  description: string;
  locais?: string;
  className?: string;
  location?: Array<number>;
  organizer?: string;
  allDay?: boolean;
  user?: number;
  takecar: string;
  leavecar: string;
  canChangeTime?: 'Y' | 'N';
  status?: string;
  carPlate?: string;
  passenger?: string;
  listPassenger?: string;
  duration?: number;
  maxPassengers?: number;
}

export interface Car {
  id: number;
  carPlate: string;
  guidCarro: string;
  capacity: number;
  available?: boolean;
  marca?: string;
  modelo?: string;
  ano_fabrico?: number;
  combustivel?: string;
  estado?: string;
  data_inicio?: Date;
  data_fim?: Date;
}

export interface ModalManagementTarget {
  modalState: boolean;
  targetCar: string;
}

export interface CarEvent {
  id: number;
  idcarro: number;
  stateId: number;
  state: string;
  startDate: string;
  endDate: string;
  observation: string;
  local: string;
  isRead: number;
  isResolved: number;
  createdBy?: string;
  createdByName?: string;
  userName?: string;
  username?: string;
  name?: string;
}

export interface OverlapEvents {
  id: number;
  carPlate: string;
  startDate: Date;
  endDate?: Date;
  stateId: number;
}

export interface OverlapTrips {
  id: number;
  groupID: string;
  start: Date;
  end?: Date;
}

export interface specificCarTrips {
  idviagem: number;
  driverName: string;
  groupID: number;
  carPlate: string;
  guidCarro: string;
  end: Date;
  start: Date;
  locais: string;
  name: string;
  usersList: string;
  end_registered: Date | null;
  start_registered: Date | null;
}

export interface DriverChange {
  id: number;
  idviagem: number;
  driverId: number;
  driverName: string;
  changeTime: Date | string;
  guidCarro: string;
}

interface User {
  id: number;
  name: string;
}

export interface CarManagmentState {
  trips: Trip[];
  plannerTrips: Trip[];
  isLoadingTrips: boolean;
  isLoadingTripRange: boolean;
  tripsError: string | null;
  carsTrips: Car[];
  carsMaintenance: Car[];
  maintenanceEventsByCar: Record<number, CarEvent[]>;
  carManagementModal: ModalManagementTarget;
  carsEvents: CarEvent[];
  isLoadingCars: boolean;
  carsError: string | null;
  CarEventsOverlap: OverlapEvents[];
  CarTripsOverlap: OverlapTrips[];
  isLoadingOverlaps: boolean;
  overlapsError: string | null;
  specificCarTrips: specificCarTrips[];
  isLoadingSpecificCarTrips: boolean;
}

interface CarManagmentContextInterface extends CarManagmentState {
  carManagmentDispatch: Dispatch<CarManagment_ACTION_TYPE>;
  refreshTrips: () => void;
  fetchTripsForRange: (
    range: PlannerDateRange,
    forceRefresh?: boolean
  ) => Promise<void>;
  refreshCarsTrips: () => void;
  refreshCarsMaintenance: () => void;
  changeModalState: (mode: boolean, target: string) => void;
  bulkUpdateTrips: (
    updates: Array<{ tripId: number; status: string; carId: number | null }>
  ) => Promise<{ success: boolean; error?: string }>;
  fetchCarMaintenanceEvents: (carID: number) => void;
  fetchCarsMaintenanceOverlap: (startDate: Date, endDate: Date) => void;
  fetchCarsTripsOverlap: (
    carID: number,
    startDate: Date,
    endDate: Date
  ) => void;
  createMaintenance: (
    updates: {
      idcarro: number;
      stateId: number;
      startDate: Date;
      endDate: Date | null;
      observation: string;
      isActive: number;
      local: string;
    },
    forceOverride?: boolean
  ) => Promise<{
    success: boolean;
    error?: string;
    conflictDetails?: Array<{
      tripId: number;
      groupId: string;
      startDate: string;
      endDate: string;
    }>;
    revokedTripIds?: number[];
  }>;
  updateMaintenance: (updates: {
    id: number;
    idcarro: number;
    stateId: number;
    startDate: Date;
    endDate: Date | null;
    observation: string;
    local: string;
    isRead: number;
    isResolved: number;
  }) => Promise<{ success: boolean; error?: string }>;
  deleteMaintenance: (updates: {
    id: number;
    idcarro: number;
  }) => Promise<{ success: boolean; error?: string }>;
  getSpecificCarTrips: (
    guidCarro: string
  ) => Promise<Trip[] | { success: false; error?: string }>;
  startTrip: (tripId: number) => Promise<{ success: boolean; error?: string }>;
  endTrip: (tripId: number) => Promise<{ success: boolean; error?: string }>;
  recordDriverChange: (tripId: number) => Promise<{
    success: boolean;
    error?: string;
  }>;
  getUsers: () => Promise<User[]>;
}

export const CarManagmentContext = createContext(
  {} as CarManagmentContextInterface
);

const apiClient = axios.create({
  baseURL: 'https://wsdigital.ctcp.pt/gcViagems',
  // baseURL: 'http://localhost:3400/gcViagems',
  withCredentials: true,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
/*apiClient.interceptors.request.use(
  config => {
    // console.log('Interceptor request config:', config);
    // Add any additional headers if needed
    // Note: httpOnly cookies are automatically included
    return config;
  },
  error => {
    // console.log('Interceptor request error:', error);
    // console.log(error);
    return Promise.reject(error);
  }
);*/

// Response interceptor for handling authentication errors
/*apiClient.interceptors.response.use(
  response => {
    // console.log('Interceptor response:', response);
    return response;
  },
  error => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      // console.log('401 Interceptor response:', error);
    }
    if (error.response?.status === 404) {
      // Token expired or invalid
      // console.log('404 Interceptor response:', error);
      // Dispatch logout action
    }
    // console.log('Interceptor response:', error);

    return Promise.reject(error);
  }
);*/

interface CarManagmentProviderProps extends PropsWithChildren {
  loadTripsOnMount?: boolean;
}

const CarManagmentProvider = ({
  children,
  loadTripsOnMount = true
}: CarManagmentProviderProps) => {
  const initialState: CarManagmentState = {
    trips: [],
    plannerTrips: [],
    isLoadingTrips: false,
    isLoadingTripRange: false,
    tripsError: null,
    carsTrips: [],
    carsMaintenance: [],
    maintenanceEventsByCar: {},
    carManagementModal: { modalState: false, targetCar: '' },
    carsEvents: [],
    isLoadingCars: false,
    carsError: null,
    CarEventsOverlap: [],
    CarTripsOverlap: [],
    isLoadingOverlaps: false,
    overlapsError: null,
    specificCarTrips: [],
    isLoadingSpecificCarTrips: false
  };

  const [CarManagmentState, carManagmentDispatch] = useReducer(
    carManagmentReducer,
    initialState
  );
  const tripRangeCacheRef = useRef(new Map<string, Trip[]>());
  const tripRangeRequestsRef = useRef(new Map<string, Promise<Trip[]>>());
  const tripRangeRequestVersionRef = useRef(0);
  const activeTripRangeRef = useRef<{
    key: string;
    requestVersion: number;
    range: PlannerDateRange;
  } | null>(null);

  const parseToDate = (
    value: Date | string | null | undefined
  ): Date | null => {
    if (value === null || value === undefined) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  };

  const formatDateForAPI = (date: Date): string => {
    const validDate = parseToDate(date);
    if (!validDate) {
      throw new Error('Invalid date passed to formatDateForAPI');
    }

    const year = validDate.getFullYear();
    const month = String(validDate.getMonth() + 1).padStart(2, '0');
    const day = String(validDate.getDate()).padStart(2, '0');
    const hours = String(validDate.getHours()).padStart(2, '0');
    const minutes = String(validDate.getMinutes()).padStart(2, '0');
    const seconds = String(validDate.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const fetchTrips = async () => {
    carManagmentDispatch({
      type: 'SET_TRIPS',
      payload: { isLoadingTrips: true, tripsError: null }
    });

    try {
      const response = await apiClient.post('/allTrips', {
        withCredentials: true
      });

      // Validate that response.data is an array
      const tripsData = Array.isArray(response.data) ? response.data : [];

      carManagmentDispatch({
        type: 'SET_TRIPS',
        payload: { trips: tripsData, isLoadingTrips: false }
      });
    } catch (error) {
      carManagmentDispatch({
        type: 'SET_TRIPS',
        payload: {
          trips: [],
          isLoadingTrips: false,
          tripsError: 'Failed to load trips'
        }
      });
    }
  };

  const fetchTripsForRange = useCallback(
    async (range: PlannerDateRange, forceRefresh = false) => {
      const startDate = new Date(range.startDate);
      const endDate = new Date(range.endDate);
      if (
        Number.isNaN(startDate.getTime()) ||
        Number.isNaN(endDate.getTime()) ||
        startDate >= endDate
      ) {
        carManagmentDispatch({
          type: 'SET_TRIPS',
          payload: {
            isLoadingTripRange: false,
            tripsError: 'Invalid trip date range'
          }
        });
        return;
      }

      const normalizedRange = { startDate, endDate };
      const key = `${startDate.toISOString()}_${endDate.toISOString()}`;
      const requestVersion = ++tripRangeRequestVersionRef.current;
      activeTripRangeRef.current = {
        key,
        requestVersion,
        range: normalizedRange
      };

      if (forceRefresh) {
        tripRangeCacheRef.current.delete(key);
        tripRangeRequestsRef.current.delete(key);
      } else {
        const cachedTrips = tripRangeCacheRef.current.get(key);
        if (cachedTrips) {
          carManagmentDispatch({
            type: 'SET_TRIPS',
            payload: {
              plannerTrips: cachedTrips,
              isLoadingTripRange: false,
              tripsError: null
            }
          });
          return;
        }
      }

      carManagmentDispatch({
        type: 'SET_TRIPS',
        payload: { isLoadingTripRange: true, tripsError: null }
      });

      let request = tripRangeRequestsRef.current.get(key);
      if (!request) {
        request = apiClient
          .post('/allTrips', {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          })
          .then(response => {
            const tripsData: Trip[] = Array.isArray(response.data)
              ? response.data
              : [];
            if (tripRangeRequestsRef.current.get(key) === request) {
              tripRangeCacheRef.current.set(key, tripsData);
            }
            return tripsData;
          })
          .finally(() => {
            if (tripRangeRequestsRef.current.get(key) === request) {
              tripRangeRequestsRef.current.delete(key);
            }
          });
        tripRangeRequestsRef.current.set(key, request);
      }

      try {
        const tripsData = await request;
        if (
          activeTripRangeRef.current?.key !== key ||
          activeTripRangeRef.current.requestVersion !== requestVersion
        ) {
          return;
        }

        carManagmentDispatch({
          type: 'SET_TRIPS',
          payload: {
            plannerTrips: tripsData,
            isLoadingTripRange: false,
            tripsError: null
          }
        });
      } catch (error) {
        if (
          activeTripRangeRef.current?.key !== key ||
          activeTripRangeRef.current.requestVersion !== requestVersion
        ) {
          return;
        }
        carManagmentDispatch({
          type: 'SET_TRIPS',
          payload: {
            isLoadingTripRange: false,
            tripsError: 'Failed to load trips for the selected weeks'
          }
        });
      }
    },
    []
  );

  const invalidateTripRangeCache = () => {
    tripRangeCacheRef.current.clear();
  };

  const fetchCarsTrips = async () => {
    carManagmentDispatch({
      type: 'SET_CARS',
      payload: { isLoadingCars: true, carsError: null }
    });

    try {
      const response = await apiClient.post('/AvailableCarsList', {
        withCredentials: true
      });

      // Validate that response.data is an array
      const carsData = Array.isArray(response.data) ? response.data : [];

      carManagmentDispatch({
        type: 'SET_CARS',
        payload: { carsTrips: carsData, isLoadingCars: false }
      });
    } catch (error) {
      carManagmentDispatch({
        type: 'SET_CARS',
        payload: {
          carsTrips: [],
          isLoadingCars: false,
          carsError: 'Failed to load carsTrips'
        }
      });
    }
  };

  const fetchCarsMaintenance = async () => {
    carManagmentDispatch({
      type: 'SET_CARS',
      payload: { isLoadingCars: true, carsError: null }
    });

    try {
      const response = await apiClient.post('/carsManagmentList', {
        withCredentials: true
      });
      // Validate that response.data is an array
      const carsData = Array.isArray(response.data) ? response.data : [];

      carManagmentDispatch({
        type: 'SET_CARS',
        payload: { carsMaintenance: carsData, isLoadingCars: false }
      });

      await Promise.all(
        carsData.map((car: Car) => fetchCarMaintenanceEvents(car.id))
      );
    } catch (error) {
      carManagmentDispatch({
        type: 'SET_CARS',
        payload: {
          carsMaintenance: [],
          isLoadingCars: false,
          carsError: 'Failed to load cars maintenances'
        }
      });
    }
  };

  const changeModalState = async (mode: boolean, target: string) => {
    carManagmentDispatch({
      type: 'SET_CARS_MANAGEMENT_MODAL',
      payload: { carManagementModal: { modalState: mode, targetCar: target } }
    });
  };

  const bulkUpdateTrips = async (
    updates: Array<{
      tripId: number;
      status: string;
      carId: number | null;
    }>
  ): Promise<{ success: boolean; error?: string }> => {
    const originalTrips = [...CarManagmentState.trips];

    try {
      // Optimistic update
      const tripUpdateMap = new Map(updates.map(u => [u.tripId, u]));

      const updatedTrips = CarManagmentState.trips.map(trip => {
        const update = tripUpdateMap.get(trip.id);
        return update
          ? {
              ...trip,
              status: update.status,
              carId: update.carId
            }
          : trip;
      });

      carManagmentDispatch({
        type: 'SET_TRIPS',
        payload: { trips: updatedTrips }
      });

      // Backend bulk update
      const response = await apiClient.post(
        '/bulkUpdateRequests',
        {
          updates
        },
        {
          withCredentials: true
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Bulk update failed');
      }

      // Refresh from server
      invalidateTripRangeCache();
      await Promise.all([fetchTrips()]);

      return { success: true };
    } catch (error) {
      // Rollback
      carManagmentDispatch({
        type: 'SET_TRIPS',
        payload: { trips: originalTrips }
      });

      return {
        success: false
      };
    }
  };

  const fetchCarMaintenanceEvents = async (carID: number) => {
    carManagmentDispatch({
      type: 'SET_CARS_STATE_LIST',
      payload: { isLoadingCars: true, carsError: null }
    });

    try {
      const response = await apiClient.post(
        '/carStatesList',
        {
          carId: carID
        },
        {
          withCredentials: true
        }
      );
      // Validate that response.data is an array
      const carStateList = Array.isArray(response.data) ? response.data : [];

      carManagmentDispatch({
        type: 'SET_CARS_STATE_LIST',
        payload: { carsEvents: carStateList, isLoadingCars: false }
      });
      carManagmentDispatch({
        type: SET_CAR_EVENTS,
        payload: { carID, events: carStateList }
      });
    } catch (error) {
      carManagmentDispatch({
        type: 'SET_CARS_STATE_LIST',
        payload: {
          carsEvents: [],
          isLoadingCars: false,
          carsError: 'Failed to load car events'
        }
      });
    }
  };

  const fetchCarsMaintenanceOverlap = async (start: Date, end: Date) => {
    carManagmentDispatch({
      type: 'SET_OVERLAPS_CAR_ALOCATION',
      payload: { isLoadingOverlaps: true, carsError: null }
    });

    try {
      const response = await apiClient.post(
        '/overlapMaintenance',
        {
          startDate: formatDateForAPI(start),
          endDate: formatDateForAPI(end)
        },
        {
          withCredentials: true
        }
      );
      // Validate that response.data is an array
      const overlapsList = Array.isArray(response.data) ? response.data : [];

      carManagmentDispatch({
        type: 'SET_OVERLAPS_CAR_ALOCATION',
        payload: { CarEventsOverlap: overlapsList, isLoadingOverlaps: false }
      });
    } catch (error) {
      carManagmentDispatch({
        type: 'SET_OVERLAPS_CAR_ALOCATION',
        payload: {
          CarEventsOverlap: [],
          isLoadingOverlaps: false,
          overlapsError: 'Failed to load overlaps'
        }
      });
    }
  };

  // check for trips at the same time we are marking a maintenance
  const fetchCarsTripsOverlap = async (
    carID: number,
    start: Date,
    end: Date | null
  ) => {
    carManagmentDispatch({
      type: 'SET_OVERLAPS_MAINTENANCE_ALOCATION',
      payload: { isLoadingOverlaps: true, carsError: null }
    });

    try {
      const response = await apiClient.post(
        '/overlapTrips',
        {
          carId: carID,
          startDate: formatDateForAPI(start),
          endDate: end ? formatDateForAPI(end) : null
        },
        {
          withCredentials: true
        }
      );
      // Validate that response.data is an array
      const overlapsList = Array.isArray(response.data) ? response.data : [];

      const normalizedOverlaps = overlapsList
        .map((item: OverlapTrips): OverlapTrips | null => {
          const startDate = parseToDate(item.start);
          if (!startDate) {
            return null;
          }

          const endDate = parseToDate(item.end);
          return {
            id: item.id,
            groupID: item.groupID,
            start,
            end: endDate || undefined
          };
        })
        .filter((item): item is OverlapTrips => item !== null);

      carManagmentDispatch({
        type: 'SET_OVERLAPS_MAINTENANCE_ALOCATION',
        payload: {
          CarTripsOverlap: normalizedOverlaps,
          isLoadingOverlaps: false
        }
      });

      return normalizedOverlaps;
    } catch (error) {
      carManagmentDispatch({
        type: 'SET_OVERLAPS_MAINTENANCE_ALOCATION',
        payload: {
          CarTripsOverlap: [],
          isLoadingOverlaps: false,
          overlapsError: 'Failed to load trips overlaps'
        }
      });
      return [];
    }
  };

  // Bulk update function (provided by user)
  const createMaintenance = async (
    updates: {
      idcarro: number;
      stateId: number;
      startDate: Date;
      endDate: Date | null;
      observation: string;
      isActive: number;
      local: string;
    },
    forceOverride = false
  ): Promise<{
    success: boolean;
    error?: string;
    conflictDetails?: Array<{
      tripId: number;
      groupId: string;
      startDate: string;
      endDate: string;
    }>;
    revokedTripIds?: number[];
  }> => {
    const formattedUpdates = {
      ...updates,
      startDate: formatDateForAPI(updates.startDate),
      endDate: updates.endDate ? formatDateForAPI(updates.endDate) : null
    };

    try {
      // 1. Fetch trips that overlap with maintenance period
      const conflictingTrips = await fetchCarsTripsOverlap(
        updates.idcarro,
        updates.startDate,
        updates.endDate
      );

      // 2. If conflicts exist, return details unless forced override is requested
      if (conflictingTrips.length > 0) {
        const conflictDetails = conflictingTrips.map(t => ({
          tripId: t.id,
          groupId: t.groupID,
          startDate: formatDateForAPI(t.start),
          endDate: t.end ? formatDateForAPI(t.end) : 'N/A'
        }));

        if (!forceOverride) {
          return {
            success: false,
            error: 'conflict',
            conflictDetails
          };
        }

        // 3. Reset conflicting trips to Pending when override is confirmed
        const overrideResult = await bulkUpdateTrips(
          conflictingTrips.map(t => ({
            tripId: t.id,
            status: 'Pendente',
            carId: null
          }))
        );

        if (!overrideResult.success) {
          return {
            success: false,
            error: overrideResult.error || 'Failed to reset trips to Pending',
            conflictDetails
          };
        }

        const revokedTripIds = conflictingTrips.map(trip => trip.id);

        const response = await apiClient.post(
          '/createMaintenance',
          formattedUpdates,
          {
            withCredentials: true
          }
        );

        if (!response.data.success) {
          throw new Error(response.data.error || 'Creation failed');
        }

        await fetchCarMaintenanceEvents(updates.idcarro);
        return { success: true, revokedTripIds };
      }

      const response = await apiClient.post(
        '/createMaintenance',
        formattedUpdates,
        {
          withCredentials: true
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Creation failed');
      }

      // Refresh all data after successful update
      await fetchCarMaintenanceEvents(updates.idcarro);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  // Edit Maintenance Event
  const updateMaintenance = async (updates: {
    id: number;
    idcarro: number;
    stateId: number;
    startDate: Date;
    endDate: Date | null;
    observation: string;
    local: string;
    isRead: number;
    isResolved: number;
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      const formattedUpdates = {
        ...updates,
        startDate: formatDateForAPI(updates.startDate),
        endDate: updates.endDate ? formatDateForAPI(updates.endDate) : null
      };

      const response = await apiClient.post(
        '/updateMaintenance',
        formattedUpdates,
        {
          withCredentials: true
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Creation failed');
      }

      // Refresh all data after successful update
      await fetchCarMaintenanceEvents(updates.idcarro);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  // Soft Delete Maintenance Event
  const deleteMaintenance = async (updates: {
    id: number;
    idcarro: number;
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.post('/deleteMaintenance', updates, {
        withCredentials: true
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Creation failed');
      }

      // Refresh all data after successful update
      await fetchCarMaintenanceEvents(updates.idcarro);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  const getSpecificCarTrips = async (
    guidCarro: string
  ): Promise<Trip[] | { success: false; error?: string }> => {
    carManagmentDispatch({
      type: 'SET_SPECIFIC_CAR_TRIPS',
      payload: { isLoadingSpecificCarTrips: true, carsError: null }
    });

    const today = new Date();
    const startDate = formatDateForAPI(
      new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0)
    );
    const endDate = formatDateForAPI(
      new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59
      )
    );

    const updatesSpecificCar = {
      guidCarro,
      startDate,
      endDate
    };
    try {
      const response = await apiClient.post(
        '/getSpecificDayCarTrips',
        updatesSpecificCar,
        {
          withCredentials: true
        }
      );

      if (!response.data.success) {
        throw new Error(
          response.data.error || 'Failed to fetch specific car trips'
        );
      }

      carManagmentDispatch({
        type: 'SET_SPECIFIC_CAR_TRIPS',
        payload: {
          specificCarTrips: response.data.results,
          isLoadingSpecificCarTrips: false
        }
      });
      return response.data.results;
    } catch (error) {
      carManagmentDispatch({
        type: 'SET_SPECIFIC_CAR_TRIPS',
        payload: {
          specificCarTrips: [],
          isLoadingSpecificCarTrips: false
        }
      });
      return [];
    }
  };

  const startTrip = async (
    tripId: number
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const startTime = new Date();
      const response = await apiClient.post(
        '/setStartTrip',
        { tripId, startTime: formatDateForAPI(startTime) },
        { withCredentials: true }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to start trip');
      }

      if (CarManagmentState.specificCarTrips.length > 0) {
        await getSpecificCarTrips(
          CarManagmentState.specificCarTrips[0].guidCarro
        );
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  const endTrip = async (
    tripId: number
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const endTime = new Date();
      const response = await apiClient.post(
        '/setEndTrip',
        { tripId, endTime: formatDateForAPI(endTime) },
        { withCredentials: true }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to end trip');
      }

      if (CarManagmentState.specificCarTrips.length > 0) {
        await getSpecificCarTrips(
          CarManagmentState.specificCarTrips[0].guidCarro
        );
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  const recordDriverChange = async (
    tripId: number
  ): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      const changeTime = new Date();

      const response = await apiClient.post(
        '/recordDriverChange',
        {
          tripId,
          changeTime: formatDateForAPI(changeTime)
        },
        { withCredentials: true }
      );

      if (!response.data.success) {
        throw new Error(
          response.data.error || 'Failed to record driver change'
        );
      }

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  const getUsers = async (): Promise<User[]> => {
    try {
      const response = await apiClient.post(
        '/getUsers',
        {},
        {
          withCredentials: true
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Failed to fetch users:', error);
      return [];
    }
  };

  // Fetch trips on mount
  useEffect(() => {
    if (loadTripsOnMount) {
      fetchTrips();
    }
    fetchCarsTrips();
    fetchCarsMaintenance();
  }, []);

  const refreshTrips = () => {
    invalidateTripRangeCache();
    const activeRange = activeTripRangeRef.current?.range;
    if (activeRange) {
      void fetchTripsForRange(activeRange, true);
    } else if (loadTripsOnMount) {
      void fetchTrips();
    }
  };

  const refreshCarsTrips = () => {
    fetchCarsTrips();
  };

  const refreshCarsMaintenance = () => {
    fetchCarsTrips();
  };

  return (
    <CarManagmentContext.Provider
      value={{
        ...CarManagmentState,
        carManagmentDispatch,
        refreshTrips,
        fetchTripsForRange,
        refreshCarsTrips,
        refreshCarsMaintenance,
        changeModalState,
        // updateTripStatus,
        bulkUpdateTrips,
        fetchCarMaintenanceEvents,
        fetchCarsMaintenanceOverlap,
        fetchCarsTripsOverlap,
        createMaintenance,
        updateMaintenance,
        deleteMaintenance,
        getSpecificCarTrips,
        startTrip,
        endTrip,
        recordDriverChange,
        getUsers
      }}
    >
      {children}
    </CarManagmentContext.Provider>
  );
};

export const useCarManagmentContext = () => useContext(CarManagmentContext);

export default CarManagmentProvider;
